import { useEffect, useRef, useState, useCallback } from "react";
import * as Icons from "lucide-react";

const DEFAULT_LEN = 1722, DEFAULT_WID = 1134; // Suntech STP440S-C54 (mm)
let gmapPromise;
function loadMaps(key) {
  if (window.google?.maps) return Promise.resolve();
  if (gmapPromise) return gmapPromise;
  gmapPromise = new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    s.async = true; s.onload = res; s.onerror = rej; document.head.appendChild(s);
  });
  return gmapPromise;
}
const metersPerPx = (lat, zoom) => 156543.03392 * Math.cos((lat * Math.PI) / 180) / Math.pow(2, zoom);

export default function GoogleRoofDesigner({ lead, value, onChange, panel, settings, dailyConsumption = 20 }) {
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const mapRef = useRef(); const searchRef = useRef();
  const mapObj = useRef(null);
  const overlay = useRef(null);
  const marker = useRef(null);
  const drag = useRef(null);
  const view = useRef({ center: value?.center || null, zoom: value?.zoom || 20 });
  const savedCenter = useRef(!!(value && value.center));
  const hydrated = useRef(false);

  const [panels, setPanels] = useState(value?.panels || []);
  const [angle, setAngle] = useState(value?.angle || 0);
  const [screen, setScreen] = useState([]); // [{x,y}] container px per panel
  const [mpp, setMpp] = useState(0.05);
  const [stringLen, setStringLen] = useState(5);
  const [mapReady, setMapReady] = useState(false);
  const [fillMode, setFillMode] = useState(false);
  const [rect, setRect] = useState(null); // {x0,y0,x1,y1} container px while drawing
  const [err, setErr] = useState(false);
  const fillDraw = useRef(false);

  const lenMm = panel?.length_mm || DEFAULT_LEN;
  const widMm = panel?.width_mm || DEFAULT_WID;
  const watts = panel?.watts || 440;

  const panelsRef = useRef(panels); panelsRef.current = panels;
  const angleRef = useRef(angle); angleRef.current = angle;

  const placeMarker = useCallback((loc) => {
    const map = mapObj.current;
    if (!map) return;
    if (marker.current) marker.current.setMap(null);
    marker.current = new window.google.maps.Marker({
      position: loc, map,
      icon: { path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW, scale: 5, fillColor: "#10b981", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 },
      title: lead?.full_address || "Property",
    });
  }, [lead?.full_address]);

  const emit = useCallback((p = panelsRef.current, a = angleRef.current) => {
    onChange({ center: view.current.center, zoom: view.current.zoom, angle: a,
      panelLenMm: lenMm, panelWidMm: widMm, panels: p });
  }, [onChange, lenMm, widMm]);

  const recompute = useCallback(() => {
    const proj = overlay.current?.getProjection?.();
    const map = mapObj.current;
    if (!proj || !map) return;
    const c = map.getCenter(); if (!c) return;
    view.current = { center: { lat: c.lat(), lng: c.lng() }, zoom: map.getZoom() };
    setMpp(metersPerPx(c.lat(), map.getZoom()));
    const pts = panelsRef.current.map((p) => {
      try {
        const px = proj.fromLatLngToContainerPixel(new window.google.maps.LatLng(p.lat, p.lng));
        return { x: px.x, y: px.y };
      } catch { return { x: -9999, y: -9999 }; }
    });
    setScreen(pts);
  }, []);

  // hydrate saved layout once (edit mode) + convert legacy fraction panels
  useEffect(() => {
    if (hydrated.current || !value) return;
    if (value.center || (value.panels && value.panels.length)) {
      hydrated.current = true;
      if (value.center) savedCenter.current = true;
      setPanels(value.panels || []);
      setAngle(value.angle || 0);
      view.current = { center: value.center || view.current.center, zoom: value.zoom || view.current.zoom };
      if (mapObj.current && value.center) { mapObj.current.setCenter(value.center); mapObj.current.setZoom(value.zoom || 20); }
    }
  }, [value]);

  useEffect(() => {
    if (!key) return;
    loadMaps(key).then(() => {
      if (!mapRef.current) return;
      const start = view.current.center || { lat: -37.88, lng: 145.16 };
      const map = new window.google.maps.Map(mapRef.current, {
        center: start, zoom: view.current.zoom, mapTypeId: "satellite", tilt: 0,
        disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy",
      });
      mapObj.current = map;
      const ov = new window.google.maps.OverlayView();
      ov.onAdd = () => {}; ov.onRemove = () => {};
      ov.draw = () => recompute();
      ov.setMap(map);
      overlay.current = ov;
      map.addListener("idle", recompute);
      map.addListener("bounds_changed", recompute);

      if (searchRef.current) {
        const ac = new window.google.maps.places.Autocomplete(searchRef.current, { componentRestrictions: { country: "au" }, fields: ["geometry"] });
        ac.addListener("place_changed", () => { const p = ac.getPlace(); if (p.geometry) { map.panTo(p.geometry.location); map.setZoom(20); placeMarker(p.geometry.location); } });
      }
      setMapReady(true);
    }).catch(() => setErr(true));
    // eslint-disable-next-line
  }, [key]);

  // Geocode the lead's property (from initial contact) once the map is ready or when the lead changes
  useEffect(() => {
    if (!mapReady || !mapObj.current) return;
    const map = mapObj.current;
    if (lead?.full_address) {
      new window.google.maps.Geocoder().geocode({ address: lead.full_address, componentRestrictions: { country: "AU" } }, (r, st) => {
        if (st === "OK" && r[0]) {
          const loc = r[0].geometry.location;
          placeMarker(loc);
          if (!savedCenter.current) { map.setCenter(loc); map.setZoom(20); }
        }
      });
    } else if (view.current.center) {
      placeMarker(view.current.center);
    }
    // eslint-disable-next-line
  }, [mapReady, lead?.full_address]);

  const centerLatLng = () => {
    const proj = overlay.current?.getProjection?.();
    const rect = mapRef.current?.getBoundingClientRect();
    if (!proj || !rect) return null;
    return proj.fromContainerPixelToLatLng(new window.google.maps.Point(rect.width / 2, rect.height / 2));
  };

  const addPanel = () => {
    const ll = centerLatLng() || (mapObj.current && mapObj.current.getCenter());
    if (!ll) return;
    const np = [...panels, { lat: ll.lat(), lng: ll.lng(), rot: 0 }];
    setPanels(np); emit(np); setTimeout(recompute, 0);
  };
  const addString = () => {
    const proj = overlay.current?.getProjection?.();
    const rect = mapRef.current?.getBoundingClientRect();
    if (!proj || !rect) return;
    const cx = rect.width / 2, cy = rect.height / 2;
    const wpx = (widMm / 1000) / metersPerPx(view.current.center.lat, view.current.zoom); // portrait width
    const gap = wpx * 0.08;
    const total = stringLen;
    const np = [...panels];
    for (let k = 0; k < total; k++) {
      const x = cx + (k - (total - 1) / 2) * (wpx + gap);
      const ll = proj.fromContainerPixelToLatLng(new window.google.maps.Point(x, cy));
      np.push({ lat: ll.lat(), lng: ll.lng(), rot: 0 });
    }
    setPanels(np); emit(np); setTimeout(recompute, 0);
  };
  const removePanel = (i) => { const np = panels.filter((_, x) => x !== i); setPanels(np); emit(np); setTimeout(recompute, 0); };
  const rotate = (i) => { const np = panels.map((p, x) => x === i ? { ...p, rot: p.rot ? 0 : 90 } : p); setPanels(np); emit(np); setTimeout(recompute, 0); };
  const clearAll = () => { setPanels([]); emit([]); setTimeout(recompute, 0); };

  // ---- Panel Fill Tool: draw a rectangle, auto-fill max panels in neat rows ----
  const panelPx = () => {
    const lat = view.current.center?.lat || -37.88;
    const m = metersPerPx(lat, view.current.zoom);
    return { w: (widMm / 1000) / m, h: (lenMm / 1000) / m }; // portrait
  };
  const fillGrid = (r) => {
    if (!r) return { rows: 0, cols: 0 };
    const { w: pw, h: ph } = panelPx();
    const gap = pw * 0.06;
    const rectW = Math.abs(r.x1 - r.x0), rectH = Math.abs(r.y1 - r.y0);
    const cols = Math.max(0, Math.floor((rectW + gap) / (pw + gap)));
    const rows = Math.max(0, Math.floor((rectH + gap) / (ph + gap)));
    return { rows, cols, pw, ph, gap };
  };
  const applyFill = (r) => {
    const proj = overlay.current?.getProjection?.();
    const box = mapRef.current?.getBoundingClientRect();
    if (!proj || !box) return;
    const { rows, cols, pw, ph, gap } = fillGrid(r);
    if (!rows || !cols) return;
    const total = Math.min(rows * cols, 400);
    const minX = Math.min(r.x0, r.x1), minY = Math.min(r.y0, r.y1);
    const rectW = Math.abs(r.x1 - r.x0), rectH = Math.abs(r.y1 - r.y0);
    const midX = minX + rectW / 2, midY = minY + rectH / 2;
    const gridW = cols * pw + (cols - 1) * gap, gridH = rows * ph + (rows - 1) * gap;
    const startX = midX - gridW / 2 + pw / 2, startY = midY - gridH / 2 + ph / 2;
    const ra = (angleRef.current || 0) * Math.PI / 180, ca = Math.cos(ra), sa = Math.sin(ra);
    const np = [...panels];
    let placed = 0;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        if (placed >= total) break;
        const lx = startX + col * (pw + gap), ly = startY + row * (ph + gap);
        const dx = lx - midX, dy = ly - midY;
        const rx = midX + dx * ca - dy * sa, ry = midY + dx * sa + dy * ca;
        const ll = proj.fromContainerPixelToLatLng(new window.google.maps.Point(rx, ry));
        np.push({ lat: ll.lat(), lng: ll.lng(), rot: 0 });
        placed++;
      }
    }
    setPanels(np); emit(np); setTimeout(recompute, 0);
  };
  const toggleFill = () => {
    const next = !fillMode;
    setFillMode(next);
    mapObj.current?.setOptions({ draggable: !next, gestureHandling: next ? "none" : "greedy", zoomControl: !next });
  };
  const px = (e) => { const box = mapRef.current.getBoundingClientRect(); const t = e.touches?.[0] || e.changedTouches?.[0] || e; return { x: t.clientX - box.left, y: t.clientY - box.top }; };
  const onFillDown = (e) => { e.preventDefault(); const p = px(e); fillDraw.current = true; setRect({ x0: p.x, y0: p.y, x1: p.x, y1: p.y }); };
  const onFillMove = (e) => { if (!fillDraw.current) return; const p = px(e); setRect((r) => ({ ...r, x1: p.x, y1: p.y })); };
  const onFillUp = () => {
    if (!fillDraw.current) return;
    fillDraw.current = false;
    setRect((r) => { if (r) applyFill(r); return null; });
    setFillMode(false);
    mapObj.current?.setOptions({ draggable: true, gestureHandling: "greedy", zoomControl: true });
  };

  const onDown = (e, i) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    mapObj.current?.setOptions({ draggable: false, gestureHandling: "none" });
    drag.current = { i };
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const proj = overlay.current?.getProjection?.();
    const rect = mapRef.current?.getBoundingClientRect();
    if (!proj || !rect) return;
    const ll = proj.fromContainerPixelToLatLng(new window.google.maps.Point(e.clientX - rect.left, e.clientY - rect.top));
    const i = drag.current.i;
    setPanels((ps) => { const np = ps.map((p, x) => x === i ? { ...p, lat: ll.lat(), lng: ll.lng() } : p); panelsRef.current = np; return np; });
    setScreen((s) => s.map((p, x) => x === i ? { x: e.clientX - rect.left, y: e.clientY - rect.top } : p));
  };
  const onUp = () => {
    if (!drag.current) return;
    drag.current = null;
    mapObj.current?.setOptions({ draggable: true, gestureHandling: "greedy" });
    emit();
  };

  const setAngleV = (a) => { setAngle(a); angleRef.current = a; emit(panels, a); };
  const centerOnProperty = () => { if (marker.current && mapObj.current) { mapObj.current.panTo(marker.current.getPosition()); mapObj.current.setZoom(20); } };

  // live metrics
  const s = settings || {};
  const kw = +(panels.length * watts / 1000).toFixed(2);
  const sun = s.daily_sun_factor || 3.8, rate = s.usage_rate || 0.30, feed = s.feed_in_tariff || 0.033;
  const annualGen = Math.round(kw * sun * 365);
  const annualCons = Math.round(dailyConsumption * 365);
  const selfUse = Math.min(annualGen, annualCons * 0.4);
  const exported = Math.max(annualGen - selfUse, 0);
  const imported = Math.max(annualCons - selfUse, 0);
  const feedIncome = exported * feed;
  const billBefore = annualCons * rate;
  const billAfter = imported * rate - feedIncome;
  const saving = Math.max(Math.round(billBefore - billAfter), 0);
  const fmt = (n) => "$" + Math.round(n).toLocaleString();

  // portrait: width = short(widMm), height = long(lenMm); rot 90 swaps
  const shortPx = (widMm / 1000) / mpp;
  const longPx = (lenMm / 1000) / mpp;

  if (!key) return <div className="text-sm text-muted-foreground p-4 border border-dashed border-border rounded-md">Add a Google Maps API key to enable satellite roof imagery.</div>;

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap gap-2 mb-3 items-center">
          <input ref={searchRef} placeholder="Search address to locate the roof…" data-testid="roof-search"
            className="flex-1 min-w-[160px] px-3 py-2 rounded-md border border-input bg-background text-sm" />
          <button type="button" onClick={centerOnProperty} data-testid="center-property"
            className="text-sm px-3 py-2 rounded-md border border-border font-semibold flex items-center gap-1.5"><Icons.MapPin size={14} /> Property</button>
          <button type="button" onClick={addPanel} data-testid="add-panel"
            className="text-sm px-3 py-2 rounded-md bg-secondary text-secondary-foreground font-semibold flex items-center gap-1.5"><Icons.Plus size={14} /> Panel</button>
          <div className="flex items-center rounded-md border border-border overflow-hidden">
            <input type="number" min="1" max="20" value={stringLen} onChange={(e) => setStringLen(Math.max(1, Math.min(20, +e.target.value || 1)))}
              data-testid="string-len" className="w-12 px-2 py-2 text-sm bg-background outline-none" />
            <button type="button" onClick={addString} data-testid="add-string"
              className="text-sm px-3 py-2 font-semibold flex items-center gap-1.5 border-l border-border"><Icons.Grid3x3 size={14} /> String</button>
          </div>
          <button type="button" onClick={toggleFill} data-testid="fill-area"
            className={`text-sm px-3 py-2 rounded-md font-semibold flex items-center gap-1.5 border ${fillMode ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>
            <Icons.Frame size={14} /> {fillMode ? "Draw roof…" : "Fill Area"}</button>
          {panels.length > 0 && <button type="button" onClick={clearAll} data-testid="clear-panels"
            className="text-sm px-3 py-2 rounded-md border border-border font-semibold flex items-center gap-1.5 text-destructive"><Icons.Trash2 size={14} /> Clear</button>}
        </div>
        <div className="flex items-center gap-2 mb-3">
          <Icons.RotateCw size={14} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Roof angle</span>
          <input type="range" min="-90" max="90" value={angle} onChange={(e) => setAngleV(+e.target.value)} data-testid="angle-slider" className="flex-1 max-w-xs" />
          <span className="text-xs font-medium w-10 text-right">{angle}°</span>
        </div>

        <div className="relative w-full rounded-md overflow-hidden border border-border bg-muted" style={{ aspectRatio: "16 / 10" }} data-testid="roof-canvas">
          <div ref={mapRef} className="absolute inset-0" />
          {err && <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground p-4 text-center">Map failed to load. Enable Maps JavaScript API on your key and check referrer restrictions.</div>}
          <div className="absolute inset-0 pointer-events-none">
            {panels.map((p, i) => {
              const sc = screen[i]; if (!sc) return null;
              const isLand = p.rot === 90;
              const w = isLand ? longPx : shortPx, h = isLand ? shortPx : longPx;
              return (
                <div key={i} onPointerDown={(e) => onDown(e, i)} onPointerMove={onMove} onPointerUp={onUp}
                  style={{ left: sc.x, top: sc.y, width: w, height: h, transform: `translate(-50%,-50%) rotate(${angle}deg)`, touchAction: "none" }}
                  className="absolute bg-emerald-500/55 border-2 border-emerald-400 rounded-[1px] cursor-move group pointer-events-auto">
                  <div className="absolute inset-[1px] grid grid-cols-3 gap-px opacity-40">
                    {Array.from({ length: 6 }).map((_, k) => <div key={k} className="bg-white/60" />)}
                  </div>
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 hidden group-hover:flex gap-1 z-10" style={{ transform: `rotate(${-angle}deg)` }}>
                    <button type="button" data-testid={`rotate-panel-${i}`} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); rotate(i); }} className="bg-card border border-border rounded p-0.5"><Icons.RotateCw size={12} /></button>
                    <button type="button" data-testid={`delete-panel-${i}`} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); removePanel(i); }} className="bg-card border border-border rounded p-0.5 text-destructive"><Icons.X size={12} /></button>
                  </div>
                </div>
              );
            })}
          </div>
          {/* fill-area draw layer */}
          {fillMode && (
            <div className="absolute inset-0 z-30" style={{ cursor: "crosshair", touchAction: "none" }}
              data-testid="fill-canvas"
              onMouseDown={onFillDown} onMouseMove={onFillMove} onMouseUp={onFillUp} onMouseLeave={onFillUp}
              onTouchStart={onFillDown} onTouchMove={onFillMove} onTouchEnd={onFillUp}>
              {!rect && <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1.5 rounded-full shadow">Drag across the roof to fill panels</div>}
              {rect && (() => {
                const g = fillGrid(rect);
                const l = Math.min(rect.x0, rect.x1), t = Math.min(rect.y0, rect.y1);
                const w = Math.abs(rect.x1 - rect.x0), h = Math.abs(rect.y1 - rect.y0);
                return (
                  <>
                    <div className="absolute border-2 border-dashed border-primary bg-primary/15" style={{ left: l, top: t, width: w, height: h }} />
                    <div className="absolute bg-primary text-primary-foreground text-xs font-bold px-2 py-1 rounded" style={{ left: l, top: Math.max(t - 24, 2) }}>
                      {g.rows * g.cols} panels ({g.cols}×{g.rows})</div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Zoom & pan freely — panels stay locked to the roof. Use <b>Fill Area</b> to drag a rectangle over the roof and auto-fill neat rows, or add a single Panel/String. Drag to reposition, hover to rotate 90° or delete. Panels drawn to real {panel ? `${panel.brand} ${panel.model_no}` : "Suntech"} size ({lenMm}×{widMm}mm).
        </p>
      </div>

      {/* live metrics */}
      <div className="lg:w-60 shrink-0" data-testid="roof-metrics">
        <div className="bg-card border border-border rounded-md p-4 sticky top-4 space-y-3">
          <div className="label-caps">Live System</div>
          <Metric icon="LayoutGrid" label="Panels placed" value={panels.length} />
          <Metric icon="Zap" label="System size" value={`${kw} kW`} accent />
          <Metric icon="SunMedium" label="Est. annual production" value={`${(annualGen / 1000).toFixed(1)} MWh`} />
          <Metric icon="PiggyBank" label="Est. annual saving" value={fmt(saving)} accent />
          {!panel && <p className="text-[11px] text-muted-foreground">Select a panel product to use its exact size & wattage.</p>}
        </div>
      </div>
    </div>
  );
}

const Metric = ({ icon, label, value, accent }) => {
  const Ic = Icons[icon] || Icons.Circle;
  return (
    <div className="flex items-center gap-2.5">
      <span className={`p-1.5 rounded-md ${accent ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}><Ic size={15} /></span>
      <div className="min-w-0">
        <div className="text-[11px] text-muted-foreground leading-tight">{label}</div>
        <div className={`font-head font-bold text-sm ${accent ? "text-primary" : ""}`}>{value}</div>
      </div>
    </div>
  );
};
